import CustomPagination from '@/platform/common/components/CustomPagination/CustomPagination';
import NoDataRow from '@/platform/common/components/NoDataRow/NoDataRow';
import SearchBarFilter from '@/platform/common/components/SearchBarFilter/SearchBarFilter';
import { useFilters } from '@/platform/hooks/useFilters';
import { useConfirmationStore } from '@/platform/stores/useConfirmationStore';
import {
    Button,
    Container,
    Menu,
    MenuItem,
    MenuItems,
    MenuSeparator,
    Segment,
    SegmentContent,
    SegmentFooter,
    SegmentHeader,
    Table,
    TableBody,
    TableCell,
    TableHeader,
    TableHeaderCell,
    TableRow,
} from '@/platform/ui';
import { MenuButton } from '@headlessui/react';
import {
    CheckIcon,
    EllipsisVerticalIcon,
    PencilIcon,
    PlusIcon,
    TrashIcon,
} from '@heroicons/react/24/outline';
import { Link, router, usePage } from '@inertiajs/react';

const IndexView = () => {
    const { auth, roles } = usePage().props;
    const { updateFilters, isSorted } = useFilters(['roles']);
    const confirm = useConfirmationStore(state => state.confirm);

    const destroy = role => {
        router.delete(`/roles/${role.id}`, {
            preserveScroll: true,
            preserveState: true,
        });
    };

    const confirmDelete = role =>
        confirm({
            title: 'Rol verwijderen?',
            description:
                'Weet je zeker dat je deze rol wilt verwijderen? Gebruikers met deze rol verliezen de bijbehorende permissies.',
            confirmLabel: 'Verwijderen',
            onConfirm: close => {
                destroy(role);
                close();
            },
        });

    return (
        <Container>
            <Segment>
                <SegmentHeader
                    header={`Rollen & Permissies (${roles?.meta?.total || 0})`}
                >
                    {['create roles', 'assign any role'].every(permission =>
                        auth?.permissions?.includes(permission)
                    ) && (
                        <Button
                            variant='primary'
                            as={Link}
                            href='/roles/create'
                        >
                            <PlusIcon className='size-5' />
                            Nieuwe rol
                        </Button>
                    )}
                </SegmentHeader>
                <SegmentContent>
                    <SearchBarFilter only={['roles']} />

                    <Table striped>
                        <TableHeader>
                            <TableRow>
                                <TableHeaderCell
                                    sorted={isSorted('name')}
                                    onClick={() =>
                                        updateFilters('sort', 'name')
                                    }
                                >
                                    Rol
                                </TableHeaderCell>
                                <TableHeaderCell
                                    className='w-px'
                                    textAlign='center'
                                >
                                    Reseller
                                </TableHeaderCell>
                                <TableHeaderCell
                                    className='w-px'
                                    textAlign='center'
                                >
                                    Contact
                                </TableHeaderCell>
                                <TableHeaderCell
                                    className='w-px'
                                    textAlign='right'
                                />
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {roles?.meta?.total === 0 && (
                                <NoDataRow numberOfColumns={4} />
                            )}

                            {roles?.data?.map(role => (
                                <TableRow key={role.id}>
                                    <TableCell>
                                        {role.can?.update ? (
                                            <Link
                                                href={`/roles/${role.id}/edit`}
                                            >
                                                {role.name}
                                            </Link>
                                        ) : (
                                            role.name
                                        )}
                                    </TableCell>
                                    <TableCell
                                        className='w-px'
                                        singleLine
                                        textAlign='center'
                                    >
                                        {role.reseller && (
                                            <CheckIcon
                                                aria-label='Reseller'
                                                className='inline size-5 text-green-600'
                                            />
                                        )}
                                    </TableCell>
                                    <TableCell
                                        className='w-px'
                                        singleLine
                                        textAlign='center'
                                    >
                                        {role.contact && (
                                            <CheckIcon
                                                aria-label='Contact'
                                                className='inline size-5 text-green-600'
                                            />
                                        )}
                                    </TableCell>
                                    <TableCell
                                        className='w-px'
                                        singleLine
                                        textAlign='right'
                                    >
                                        {(role.can?.update ||
                                            role.can?.delete) && (
                                            <Menu>
                                                <MenuButton
                                                    aria-label={`Acties voor ${role.name}`}
                                                >
                                                    <EllipsisVerticalIcon className='size-5' />
                                                </MenuButton>
                                                <MenuItems anchor='bottom end'>
                                                    {role.can?.update && (
                                                        <>
                                                            <MenuItem
                                                                as={Link}
                                                                href={`/roles/${role.id}/edit`}
                                                                icon={
                                                                    PencilIcon
                                                                }
                                                            >
                                                                Wijzigen
                                                            </MenuItem>

                                                            <MenuSeparator />
                                                        </>
                                                    )}
                                                    {role.can?.delete && (
                                                        <MenuItem
                                                            className='text-red-600'
                                                            onClick={() =>
                                                                confirmDelete(
                                                                    role
                                                                )
                                                            }
                                                            icon={TrashIcon}
                                                        >
                                                            Verwijderen
                                                        </MenuItem>
                                                    )}
                                                </MenuItems>
                                            </Menu>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </SegmentContent>
                <SegmentFooter>
                    <CustomPagination meta={roles?.meta} only={['roles']} />
                </SegmentFooter>
            </Segment>
        </Container>
    );
};

export default IndexView;
